import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  TCCredential,
  TCCredentialCreateBodyParams,
  TCCredentialRevealSecretResponse,
  TCCredentialUpdateBodyParams,
  TCCredentialUpdateSecretBodyParams,
  TCCredentialsListQueryParams,
  TCCredentialsListResponse,
  V1CredentialsService,
} from 'src/api/generated';
import { ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';

/**
 * Parameters for the useCredentials hook.
 */
export interface UseCredentialsParams extends TCCredentialsListQueryParams {
  /** Whether the query is enabled. Defaults to true. Disable to prevent query execution. */
  enabled?: boolean;
}

/**
 * Response object returned by the useCredentials hook.
 */
export interface UseCredentialsResponse extends ApiResponse {
  /** Array of credentials from the current pages. */
  credentials: TCCredential[];
  /** Function to fetch the next page of results. */
  fetchNextPage: () => Promise<unknown>;
  /** Whether there are more pages available to fetch. */
  hasNextPage: boolean;
  /** Whether a next page is currently being fetched. */
  isFetchingNextPage: boolean;
  /** Token for fetching the next page of results. */
  nextPageToken?: string;
  /** Total count of credentials matching the query filters. */
  totalCount?: number;
}

/**
 * Hook for fetching a paginated list of credentials.
 *
 * @param params - Pagination and filter parameters.
 * @returns Response object containing credentials array, pagination state, and control functions.
 */
export const useCredentials = (
  params: UseCredentialsParams = {}
): UseCredentialsResponse => {
  const { enabled = true, limit, provider, kind } = params;

  const fetchCredentials = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<TCCredentialsListResponse> => {
    return await V1CredentialsService.listCredentials(
      limit,
      pageParam,
      provider,
      kind
    );
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['credentials', { limit, provider, kind }],
    queryFn: fetchCredentials,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: undefined,
    enabled,
  });

  const credentials = useMemoizedArrayData<TCCredential>(
    data?.pages.flatMap((page) => page.items) ?? []
  );
  const totalCount = data?.pages[0]?.totalCount;
  const nextPageToken = data?.pages?.at(-1)?.nextPageToken;

  return {
    loading,
    error,
    credentials,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalCount,
    nextPageToken,
    refetch,
  };
};

export interface UseCredentialResponse extends ApiResponse {
  credential: TCCredential | null;
}

export const useCredential = (
  credentialId: string | undefined,
  enabled = true
): UseCredentialResponse => {
  const fetcher = async (): Promise<TCCredential> => {
    if (!credentialId) {
      throw new Error('Missing credentialId');
    }

    return await V1CredentialsService.getCredential(credentialId);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery<TCCredential>({
    queryKey: ['credentials', credentialId],
    queryFn: fetcher,
    enabled: enabled && !!credentialId,
  });

  return {
    loading,
    error,
    credential: data ?? null,
    refetch,
  };
};

/**
 * Hook for creating a credential.
 */
export const useCreateCredential = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (body: TCCredentialCreateBodyParams) => {
    return await V1CredentialsService.createCredential(body);
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
    },
  });

  return {
    createCredential: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

/**
 * Hook for updating credential metadata (no secret changes).
 */
export const useUpdateCredential = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: {
    credentialId: string;
    body: TCCredentialUpdateBodyParams;
  }) => {
    return await V1CredentialsService.updateCredential(
      params.credentialId,
      params.body
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
    },
  });

  return {
    updateCredential: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

/**
 * Hook for rotating a credential secret (write-only).
 */
export const useRotateCredentialSecret = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: {
    credentialId: string;
    body: TCCredentialUpdateSecretBodyParams;
  }) => {
    return await V1CredentialsService.updateCredentialSecret(
      params.credentialId,
      params.body
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
    },
  });

  return {
    rotateCredentialSecret: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

/**
 * Hook for deleting a credential.
 */
export const useDeleteCredential = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (credentialId: string) => {
    return await V1CredentialsService.deleteCredential(credentialId);
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
    },
  });

  return {
    deleteCredential: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};

/**
 * Hook for revealing a credential secret payload (super admin only).
 *
 * @remarks
 * This returns sensitive data and should only be used in tightly scoped UI.
 */
export const useRevealCredentialSecret = () => {
  const mutationFn = async (
    credentialId: string
  ): Promise<TCCredentialRevealSecretResponse> => {
    return await V1CredentialsService.revealCredentialSecret(credentialId);
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
  });

  return {
    revealCredentialSecret: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};
